import { useCallback, useState, useEffect } from "react";
import {
  Loader2,
  Check,
  AlertCircle,
  PanelRightClose,
  PanelRightOpen,
  Eye,
  EyeOff,
} from "lucide-react";
import { Button } from "@learn-me/ui";
import { SectionPlayer, type Section } from "@/components/content";
import { SectionListPanel } from "./SectionListPanel";
import { EditableSectionContent } from "./EditableSectionContent";
import { AudioAdminControls } from "./AudioAdminControls";
import { ChapterConfigPanel } from "./ChapterConfigPanel";
import { ChapterQuizPanel } from "./ChapterQuizPanel";
import { BulkAudioPanel } from "./BulkAudioPanel";
import { ChapterAITools } from "../ChapterAITools";
import { useWYSIWYGEditor } from "./useWYSIWYGEditor";
import { StudentPreviewMode } from "./StudentPreviewMode";
import toast from "react-hot-toast";

interface WYSIWYGChapterEditorProps {
  chapterId: string;
  courseId: string;
  chapterTitle?: string;
}

/**
 * Editor WYSIWYG de capítulos.
 * Muestra el SectionPlayer real con capacidad de edición inline.
 */
export function WYSIWYGChapterEditor({
  chapterId,
  courseId,
  chapterTitle: initialTitle,
}: WYSIWYGChapterEditorProps) {
  const { state, actions, isLoading, error } = useWYSIWYGEditor(chapterId);
  const [showRightPanel, setShowRightPanel] = useState(true);
  const [chapterTitle, setChapterTitle] = useState(initialTitle || "");
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Callback for when title changes in config panel
  const handleTitleChange = useCallback((title: string) => {
    setChapterTitle(title);
  }, []);

  // Convert ChapterSection to Section format for SectionPlayer
  const sectionsForPlayer: Section[] = state.sections.map((s) => ({
    id: s.id,
    title: s.title,
    content: s.content,
    audioUrl: s.audioUrl || undefined,
    audioDuration: s.audioDuration || undefined,
    position: s.position,
    featuredImageUrl: s.featuredImageUrl || undefined,
  }));

  // Handle content click - start editing
  const handleContentClick = useCallback(
    (sectionId: string) => {
      actions.startEditing(sectionId, "content");
    },
    [actions],
  );

  // Handle title click - start editing title
  const handleTitleClick = useCallback(
    (sectionId: string) => {
      actions.startEditing(sectionId, "title");
    },
    [actions],
  );

  // Handle add section (manual or AI-generated)
  const [isAddingSection, setIsAddingSection] = useState(false);
  const handleAddSection = useCallback(
    async (options?: { withAI?: boolean }) => {
      setIsAddingSection(true);
      try {
        await actions.addSection(state.currentSectionIndex, options);
        toast.success(
          options?.withAI ? "Sección generada con IA" : "Sección creada",
        );
      } catch {
        toast.error(
          options?.withAI
            ? "Error al generar sección con IA"
            : "Error al crear sección",
        );
      } finally {
        setIsAddingSection(false);
      }
    },
    [actions, state.currentSectionIndex],
  );

  // Handle delete section
  const handleDeleteSection = useCallback(
    async (sectionId: string) => {
      if (!window.confirm("¿Eliminar esta sección?")) return;
      try {
        await actions.deleteSection(sectionId);
        toast.success("Sección eliminada");
      } catch {
        toast.error("Error al eliminar sección");
      }
    },
    [actions],
  );

  // Custom render for editable content
  const renderEditableContent = useCallback(
    (section: Section) => {
      const isEditing =
        state.editingState.isEditing &&
        state.editingState.sectionId === section.id &&
        state.editingState.field === "content";

      return (
        <EditableSectionContent
          section={section}
          isEditing={isEditing}
          editingValue={
            isEditing ? state.editingState.currentValue : section.content
          }
          onContentChange={actions.updateContent}
          onStartEditing={() => actions.startEditing(section.id, "content")}
          onStopEditing={actions.saveAndStopEditing}
          chapterId={chapterId}
          onContentApplied={actions.refetchSections}
        />
      );
    },
    [state.editingState, actions, chapterId],
  );

  // Custom render for editable title
  const renderEditableTitle = useCallback(
    (section: Section) => {
      const isEditing =
        state.editingState.isEditing &&
        state.editingState.sectionId === section.id &&
        state.editingState.field === "title";

      if (isEditing) {
        return (
          <input
            type="text"
            value={state.editingState.currentValue}
            onChange={(e) => actions.updateContent(e.target.value)}
            onBlur={() => actions.saveAndStopEditing()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                actions.saveAndStopEditing();
              } else if (e.key === "Escape") {
                e.preventDefault();
                actions.cancelEditing();
              }
            }}
            autoFocus
            className="text-2xl font-bold text-gray-900 mb-4 w-full bg-transparent border-b-2 border-primary-500 outline-none px-1 -mx-1"
          />
        );
      }

      return (
        <h2
          onClick={() => actions.startEditing(section.id, "title")}
          className="text-2xl font-bold text-gray-900 mb-4 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors -mx-2 px-2 py-1 group"
        >
          {section.title}
          <span className="opacity-0 group-hover:opacity-100 text-xs text-gray-400 ml-2 font-normal">
            Clic para editar
          </span>
        </h2>
      );
    },
    [state.editingState, actions],
  );

  // Render audio admin controls below the audio player
  const renderAudioControls = useCallback(
    (section: Section) => (
      <AudioAdminControls
        sectionId={section.id}
        hasAudio={!!section.audioUrl}
        onAudioChanged={actions.refetchSections}
      />
    ),
    [actions],
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when editing content
      if (state.editingState.isEditing) {
        // Escape to cancel editing
        if (e.key === "Escape") {
          e.preventDefault();
          actions.cancelEditing();
        }
        // Ctrl/Cmd + S to save
        if ((e.ctrlKey || e.metaKey) && e.key === "s") {
          e.preventDefault();
          actions.saveAndStopEditing();
        }
        return;
      }

      // Navigation shortcuts (only when not editing)
      switch (e.key) {
        case "ArrowUp":
        case "ArrowLeft":
          if (!e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            actions.goToPrev();
          }
          break;
        case "ArrowDown":
        case "ArrowRight":
          if (!e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            actions.goToNext();
          }
          break;
        case "Enter":
          // Enter to start editing current section
          if (!e.ctrlKey && !e.metaKey && state.currentSectionId) {
            e.preventDefault();
            actions.startEditing(state.currentSectionId, "content");
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [state.editingState.isEditing, state.currentSectionId, actions]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-red-500">
        <AlertCircle className="h-12 w-12 mb-4" />
        <p>Error al cargar el capítulo</p>
        <p className="text-sm text-gray-500 mt-2">{error.message}</p>
      </div>
    );
  }

  if (state.sections.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <p className="text-gray-500 mb-4">Este capítulo no tiene secciones</p>
        <button
          onClick={() => handleAddSection()}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
        >
          Crear primera sección
        </button>
      </div>
    );
  }

  const hasSections = state.sections.length > 0;

  // Preview mode - show student view
  if (isPreviewMode) {
    return (
      <div className="flex flex-col h-full bg-gray-50 overflow-hidden">
        {/* Status Bar in Preview Mode */}
        <div className="flex items-center justify-between px-4 py-2 border-b bg-amber-50">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">
              Vista previa - Así verá el estudiante
            </span>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={() => setIsPreviewMode(false)}
          >
            <EyeOff className="h-4 w-4 mr-1" />
            Volver a Editar
          </Button>
        </div>

        {/* Student Preview */}
        <div className="flex-1 overflow-y-auto">
          <StudentPreviewMode
            chapterId={chapterId}
            courseId={courseId}
            sections={sectionsForPlayer}
            currentSectionIndex={state.currentSectionIndex}
            onSectionChange={actions.goToSection}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-gray-50 overflow-hidden">
      {/* Left Panel: Section List */}
      <SectionListPanel
        sections={state.sections}
        currentIndex={state.currentSectionIndex}
        onSectionClick={actions.goToSection}
        onAddSection={handleAddSection}
        onDeleteSection={handleDeleteSection}
        onReorderSections={actions.reorderSections}
        isCollapsed={state.isPanelCollapsed}
        isAddingSection={isAddingSection}
      />

      {/* Center Panel: Live Preview with Editable Content */}
      <div className="flex-1 flex flex-col min-w-0 bg-white border-x border-gray-200">
        {/* Status Bar */}
        <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50">
          <div className="text-sm text-gray-600">
            {chapterTitle && (
              <span className="font-medium">{chapterTitle}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {state.isSaving && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Loader2 className="h-3 w-3 animate-spin" />
                Guardando...
              </span>
            )}
            {!state.isSaving && state.lastSavedAt && (
              <span className="flex items-center gap-1 text-xs text-green-600">
                <Check className="h-3 w-3" />
                Guardado
              </span>
            )}
            {state.editingState.isDirty && (
              <span
                className="w-2 h-2 rounded-full bg-yellow-500"
                title="Cambios sin guardar"
              />
            )}
            <Button
              variant={isPreviewMode ? "default" : "outline"}
              size="sm"
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              className="ml-2"
              title={isPreviewMode ? "Volver a editar" : "Vista estudiante"}
            >
              {isPreviewMode ? (
                <EyeOff className="h-4 w-4 mr-1" />
              ) : (
                <Eye className="h-4 w-4 mr-1" />
              )}
              {isPreviewMode ? "Editar" : "Vista Estudiante"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowRightPanel(!showRightPanel)}
              className="ml-2"
              title={showRightPanel ? "Ocultar panel" : "Mostrar panel"}
            >
              {showRightPanel ? (
                <PanelRightClose className="h-4 w-4" />
              ) : (
                <PanelRightOpen className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Section Player with Editable Mode */}
        <div className="flex-1 overflow-y-auto p-6">
          <SectionPlayer
            sections={sectionsForPlayer}
            initialSection={state.currentSectionIndex}
            onSectionChange={actions.goToSection}
            editable
            onContentClick={handleContentClick}
            onTitleClick={handleTitleClick}
            renderContent={renderEditableContent}
            renderTitle={renderEditableTitle}
            renderAudioControls={renderAudioControls}
          />
        </div>
      </div>

      {/* Right Panel: Configuration & Tools */}
      {showRightPanel && (
        <div className="w-80 flex-shrink-0 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {/* Chapter Configuration */}
          <ChapterConfigPanel
            chapterId={chapterId}
            onTitleChange={handleTitleChange}
          />

          {/* Quiz Panel */}
          <ChapterQuizPanel
            chapterId={chapterId}
            courseId={courseId}
            hasSections={hasSections}
          />

          {/* Bulk Audio Panel */}
          <BulkAudioPanel
            chapterId={chapterId}
            onAudioGenerated={actions.refetchSections}
          />

          {/* AI Tools */}
          <ChapterAITools
            chapterId={chapterId}
            courseId={courseId}
            hasSections={hasSections}
            onSectionsGenerated={() => {
              actions.refetchSections();
            }}
          />
        </div>
      )}
    </div>
  );
}
